import React from 'react';
import { X } from 'phosphor-react';
import usePresence from '../../use/usePresence';

interface ActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /**
   * Para o conteúdo que traz a própria moldura sangrada (cabeçalho colorido de
   * ponta a ponta). Sem isto o `px-6 pb-6 pt-2` daqui dobra com o padding de lá.
   */
  semPadding?: boolean;
}

const sizes: Record<string, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
};

/**
 * Shell de modal do app: folha que sobe no mobile, caixa centrada no desktop.
 *
 * A montagem passa pelo `usePresence` para a saída existir — antes era
 * `if (!isOpen) return null` e o modal sumia no mesmo frame. Cinco modais
 * (DeleteProduct, ProductDetail, RoutineSuggestion, StepRemovalReason e o de
 * categoria do editor) herdam isso sem mudar uma linha.
 *
 * O par de animações muda no `sm:` de propósito: subir 100% da tela é a leitura
 * certa para folha de baixo e a errada para uma caixa no meio do desktop.
 */
export default function ActionModal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  semPadding = false,
}: ActionModalProps) {
  const { montado, estado } = usePresence(isOpen, 200);

  if (!montado) return null;

  return (
    <div
      data-state={estado}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 data-[state=closed]:pointer-events-none"
    >
      <div
        data-state={estado}
        className="fixed inset-0 bg-black/30 backdrop-blur-sm data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        data-state={estado}
        className={`
          relative
          w-full ${sizes[size]}
          bg-white dark:bg-gray-800
          rounded-t-2xl sm:rounded-2xl
          shadow-soft-lg dark:shadow-none dark:border dark:border-gray-700
          overflow-hidden max-h-[90vh] flex flex-col
          data-[state=open]:animate-sheet-in data-[state=closed]:animate-sheet-out
          sm:data-[state=open]:animate-scale-in sm:data-[state=closed]:animate-scale-out
        `}
      >
        {title && (
          <div className="flex items-center justify-between px-6 pt-6 pb-2 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">{title}</h2>
            <button
              onClick={onClose}
              className="p-1.5 text-nude-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-nude-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
              aria-label="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        <div className={`overflow-y-auto ${semPadding ? '' : 'px-6 pb-6 pt-2'}`}>
          {children}
        </div>

        <div className="h-[env(safe-area-inset-bottom)] sm:hidden" />
      </div>
    </div>
  );
}
